import { useEffect, useState } from "react";

import isEmpty from "lodash/isEmpty";

import { WeightTrackingAnalyticsSources } from "@/constants/analytics/Analytics";
import { TabSources } from "@/constants/analytics/Tabs";
import { WeightTrackingTestID } from "@/constants/TestID";
import { useAnalytics } from "@/services/Analytics";
import { useGetWeightObservationsQuery } from "@/store/api/vitals";
import {
  useAppUserProxyPatientsInTreatmentSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import { CardDimensions } from "@/ui/components/home/HomeResourcesCard";
import WeightTrackingOverview from "@/ui/components/vitals/overview/WeightTrackingOverview";
import { SectionHeader } from "@/ui/core";
import { Box, CarouselWrapper } from "@/ui/layout";
import { isWeb } from "@/utils/platform.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { getTabSelectorOptions } from "@/utils/user.util";

const WeightTrackingSection = () => {
  const { trackUserSelectorEvent } = useAnalytics();
  const {
    externalId: appUserId,
    isPatient: isAppUserPatient,
    isSupport: isAppUserSupport,
  } = useAppUserSelector();
  const inTreatmentPatients = useAppUserProxyPatientsInTreatmentSelector();

  const userTabs = getTabSelectorOptions({
    appUserCount: 0,
    appUserId,
    isAppUserPatient,
    patients: inTreatmentPatients,
    shouldIncludeSelf: false,
  });

  const [selectedUserId, setSelectedUserId] = useState<string>("");

  useEffect(() => {
    if (isEmpty(selectedUserId) && isNotNilNotEmpty(userTabs)) {
      setSelectedUserId(userTabs[0].userId);
    }
  }, [userTabs, selectedUserId, appUserId]);

  const patientExternalId = selectedUserId;

  const { data: queryData } = useGetWeightObservationsQuery(
    {
      pageNumber: 1,
      patientExternalId,
    },
    {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      skip: isEmpty(patientExternalId),
    },
  );
  const data = queryData?.data || [];

  // If the app user is a patient or there are no tabs, we don't render the component
  if (isAppUserPatient || !userTabs?.length) {
    return null;
  }

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId);

    trackUserSelectorEvent({
      component: WeightTrackingAnalyticsSources.Section,
      selectedUser: userId,
      source: TabSources.Home,
    });
  };
  const [alignSelf, width, maxWidth] = isWeb()
    ? ["center" as const, CardDimensions.WebMaxWidth, "100%"]
    : [undefined, undefined, undefined];

  return (
    <CarouselWrapper
      hasPaddingTop={false}
      header={
        <Box paddingBottom={6}>
          <SectionHeader
            appUserId={appUserId}
            isAppUserSupport={isAppUserSupport}
            isInlineHeader
            onUserChange={handleUserChange}
            testID={WeightTrackingTestID.Header}
            title="Weight"
            userTabs={userTabs}
          />
        </Box>
      }
    >
      <Box
        alignSelf={alignSelf}
        maxWidth={maxWidth}
        paddingHorizontal={24}
        width={width}
      >
        <WeightTrackingOverview
          data={data}
          selectedUserId={patientExternalId}
        />
      </Box>
    </CarouselWrapper>
  );
};

export default WeightTrackingSection;
